<template>
  <footer itemscope itemtype="https://schema.org/Organization">
    <section class="py-16 md:py-20 bg-[#1a233f] text-gray-50">
      <div class="max-w-5xl mx-auto px-4 text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-4">{{ t('footer.contact.title') }}</h2>
        <p class="text-gray-300 max-w-2xl mx-auto mb-10">{{ t('footer.contact.subtitle') }}</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <div class="bg-gray-50/5 border border-gray-50/10 rounded-xl p-8 hover:bg-gray-50/10 transition-colors">
            <i class="pi pi-send text-4xl text-[#2db9d6] mb-4"></i>
            <h3 class="text-xl font-semibold mb-2">{{ t('footer.contact.formTitle') }}</h3>
            <p class="text-sm text-gray-300 mb-5">{{ t('footer.contact.formSubtitle') }}</p>
            <RouterLink :to="localeTo({ name: 'SearchEnquiryPage' })">
              <AppButton :label="t('footer.contact.enquiry')" icon="pi pi-arrow-right" iconPos="right" size="medium" />
            </RouterLink>
          </div>

          <div class="bg-gray-50/5 border border-gray-50/10 rounded-xl p-8 hover:bg-gray-50/10 transition-colors">
            <i class="pi pi-phone text-4xl text-[#2db9d6] mb-4"></i>
            <h3 class="text-xl font-semibold mb-2">{{ t('footer.contact.callTitle') }}</h3>
            <p class="text-sm text-gray-300 mb-5">{{ t('footer.contact.callSubtitle') }}</p>
            <Button
              :label="t('header.phone')"
              icon="pi pi-phone"
              outlined
              class="!border-gray-50 !text-gray-50 hover:!bg-gray-50 hover:!text-[#1a233f]"
              as="a"
              :href="`tel:${t('header.phone').replace(/\s/g, '')}`"
            />
          </div>
        </div>
      </div>
    </section>

    <div class="bg-[#1a233f] text-gray-300 border-t border-white/10">
      <div class="max-w-7xl mx-auto px-4 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
        <!-- Logo and address -->
        <div class="lg:col-span-1">
          <div class="flex flex-col items-start">
            <img :src="logoLight" alt="GMG Automation" class="h-14 w-auto flex-shrink-0 mb-3 lg:mb-4" />
            <div class="text-sm leading-relaxed space-y-0.5">
              <p>GMG Automation IT Srl Unipersonale</p>
              <p>Via Francesco Crispi 30/A</p>
              <p>34125 Trieste (TS)</p>
              <p>Italia</p>
              <p class="pt-4">VAT: IT01448720324</p>
            </div>
          </div>
        </div>

        <!-- Manufacturers -->
        <div>
          <h3 class="text-white font-semibold mb-4 uppercase tracking-wide text-sm">
            {{ t('footer.sections.manufacturers') }}
          </h3>
          <ul class="space-y-2 text-sm">
            <li v-for="m in manufacturers" :key="m.id">
              <RouterLink
                :to="localeTo({
                  name: 'ManufacturerProductsPage',
                  params: { manufacturerSlug: m.name.toLowerCase().replace(/\s+/g, '-') },
                })"
                class="link"
              >
                {{ m.name }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <!-- Quick Links -->
        <div>
          <h3 class="text-white font-semibold mb-4 uppercase tracking-wide text-sm">
            {{ t('footer.sections.quickLinks') }}
          </h3>
          <ul class="space-y-2 text-sm">
            <li v-for="link in quickLinks" :key="link.key">
              <RouterLink :to="localeTo({ name: link.name })" class="link">{{ t(link.key) }}</RouterLink>
            </li>
          </ul>
        </div>

        <!-- Contact -->
        <div class="lg:col-span-1">
          <h3 class="text-white font-semibold mb-4 uppercase tracking-wide text-sm">
            {{ t('footer.sections.connect') }}
          </h3>
          <ul class="space-y-3 text-sm">
            <li>
              <a :href="`tel:${t('header.phone').replace(/\s/g, '')}`" class="flex items-center gap-1 md:gap-2 link" itemprop="telephone">
                <i class="pi pi-phone"></i>
                {{ t('header.phone') }}
              </a>
            </li>
            <li>
              <a href="mailto:webenquiries@gmgautomation.net" class="flex items-center gap-1 md:gap-2 link break-all" itemprop="email">
                <i class="pi pi-envelope shrink-0"></i>
                webenquiries@gmgautomation.net
              </a>
            </li>
          </ul>

          <div class="flex items-center gap-4 mt-5">
            <a
              :href="socialLinks.linkedin"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              class="text-gray-400 hover:text-[#0A66C2] transition-colors"
            >
              <i class="pi pi-linkedin text-3xl"></i>
            </a>
            <a
              :href="socialLinks.facebook"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              class="text-gray-400 hover:text-[#1877F2] transition-colors"
            >
              <i class="pi pi-facebook text-3xl"></i>
            </a>
            <a
              :href="socialLinks.instagram"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              class="text-gray-400 hover:text-[#E1306C] transition-colors"
            >
              <i class="pi pi-instagram text-3xl"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="border-t border-white/5 px-4 py-4">
        <p class="max-w-7xl mx-auto text-xs text-gray-400 text-center leading-relaxed">
          {{ t('footer.disclaimer') }}
        </p>
      </div>

      <div class="border-t border-white/10">
        <div
          class="max-w-7xl mx-auto px-4 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-400"
        >
          <p>© {{ currentYear }} GMG Automation. {{ t('footer.legal.rights') }}</p>
          <p class="text-gray-500">
            {{ t('footer.recaptcha') }}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              class="underline hover:text-gray-400 transition-colors"
            >
              {{ t('footer.recaptchaPrivacy') }}
            </a>
            &
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              class="underline hover:text-gray-400 transition-colors"
            >
              {{ t('footer.recaptchaTerms') }}
            </a>
          </p>
          <div class="flex items-center gap-5">
            <RouterLink :to="localeTo({ name: 'PrivacyPolicyPage' })" class="link">{{ t('footer.legal.privacy') }}</RouterLink>
            <RouterLink :to="localeTo({ name: 'TermsPage' })" class="link">{{ t('footer.legal.terms') }}</RouterLink>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { socialLinks, quickLinks } from '@/data/general/footerLinks'
import AppButton from '@/components/general/ui/AppButton.vue'
import { RouterLink } from 'vue-router'
import { useManufacturersStore } from '@/stores/general/manufacturersStore'
import { storeToRefs } from 'pinia'
import { useLocaleTo } from '@/composables/useLocaleTo'

const { t } = useI18n({ useScope: 'global' })
const { localeTo } = useLocaleTo()
const store = useManufacturersStore()
const { manufacturers } = storeToRefs(store)

const logoLight = new URL('@/assets/images/logo_light.svg', import.meta.url).href
const currentYear = new Date().getFullYear()
</script>
